'use client'
import AppIcon from "@/app/components/atomic/AppIcon"
import {getWPPageComments} from "@/app/endpoints/wpEndpoints"
import {IWPComment, IWPCommentsPayload} from "@/app/models/wp"
import * as Sentry from "@sentry/nextjs"
import Image from "next/image"
import {useState} from "react"
import {useTracking} from "@/app/contexts/tracking-context"
import {ArticleTrackingParams} from "@/app/utils/article-tracking"
import logoRM from "@/public/images/RM_logo_V2_mini.svg"

const getFormattedDate = (date: string) => {
    const dateOptions: Intl.DateTimeFormatOptions = {year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Europe/Paris'};
    const dateObj = new Date(date ?? '');
    return dateObj.toLocaleDateString('fr-FR', dateOptions);
}

function getInitials(name?: string): string {
    return (name ?? '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('')
}

function decodeHTMLEntities(text: string): string {
    return text
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
        .replace(/&mdash;/g, '—')
        .replace(/&ndash;/g, '–')
}

export default function PageCommentsSection({wpPageId, comments, articleParams}: {
    wpPageId: string,
    comments: IWPCommentsPayload,
    articleParams?: ArticleTrackingParams
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [commentsDisplayed, setCommentsDisplayed] = useState<IWPComment[]>(comments.comments)

    async function fetchAllComments() {
        setIsLoading(true)
        try {
            setCommentsDisplayed((await getWPPageComments(wpPageId)).comments)
        } catch (error) {
            Sentry.captureException(error)
        } finally {
            setIsLoading(false)
        }
    }

    const updateComment = (commentId: string, updates: Partial<IWPComment>) => {
        setCommentsDisplayed(prevComments =>
            prevComments.map(comment =>
                comment.id === commentId
                    ? {...comment, ...updates}
                    : comment
            )
        )
    }

    return <>
        {
            commentsDisplayed.map((comment) => (
                <Comment key={comment.id} comment={comment} onUpdateComment={updateComment} articleParams={articleParams} />
            ))
        }

        {commentsDisplayed.length < comments.totalComments && (
            <button
                className="flex w-fit items-center gap-2 rounded-xl px-5 py-3 font-semibold ring-1 ring-inset ring-gray-200 hover:bg-neutral"
                onClick={() => fetchAllComments()}
                disabled={isLoading}
            >
                {isLoading
                    ? <><AppIcon type={"regular"} iconClassName={"fa-spinner fa-spin"} /> Chargement</>
                    : <>Voir plus de commentaires <AppIcon type={"regular"} iconClassName={"fa-chevron-down"} /></>
                }
            </button>
        )}
    </>
}

export function Comment({comment, onUpdateComment, isRoot = true, articleParams}: {
    comment: IWPComment,
    onUpdateComment: (commentId: string, updates: Partial<IWPComment>) => void,
    isRoot?: boolean,
    articleParams?: ArticleTrackingParams
}) {
    const [isLikeLoading, setIsLikeLoading] = useState(false)
    const {track} = useTracking()

    const handleLikeToggle = async () => {
        if (isLikeLoading) return

        setIsLikeLoading(true)
        try {
            const response = await fetch('/api/comments/toggle-like', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    commentId: comment.id
                })
            })

            if (!response.ok) {
                throw new Error(`Toggle like failed with status ${response.status}`)
            }

            const result = await response.json()

            if (result.success) {
                onUpdateComment(comment.id, {
                    isLiked: result.isLiked,
                    totalLikes: result.totalLikes
                })

                track({event: 'like_commentaire', ...articleParams})
            }
        } catch (error) {
            Sentry.captureException(error)
        } finally {
            setIsLikeLoading(false)
        }
    }

    return (
        <article className={`flex flex-col gap-3 ${isRoot ? "rounded-2xl bg-white p-6 shadow-[0_1px_4px_rgba(0,0,0,0.25)]" : ""}`}>
            <div className="flex flex-col gap-3 w-full">
                <div className="flex items-center gap-3.5">
                    <div className="flex size-[45px] shrink-0 items-center justify-center rounded-full bg-rm-blue font-bold text-white">
                        {getInitials(comment.authorName)}
                    </div>

                    <div className="flex flex-col">
                        <span className="font-bold">{comment.authorName}</span>
                        <span className="text-sm text-rm-gray">Le {getFormattedDate(comment.date)}</span>
                    </div>
                </div>

                {comment.title &&
                    <h3 className="font-semibold text-rm-16">
                        {comment.title}
                    </h3>
                }

                <div dangerouslySetInnerHTML={{__html: comment.commentContent}} />
            </div>

            {comment.response &&
                <Answer comment={comment.response} onUpdateComment={onUpdateComment} articleParams={articleParams} />
            }

            {isRoot &&
                <div className="ml-auto">
                    <button
                        type="button"
                        onClick={handleLikeToggle}
                        disabled={isLikeLoading}
                        aria-pressed={comment.isLiked}
                        aria-label={comment.isLiked ? 'Retirer mon like' : 'Liker ce commentaire'}
                        className={`flex items-center justify-center rounded-[100px] px-4 py-1 text-rm-12 font-semibold
                                    hover:bg-blue-200 hover:text-rm-blue transition-all duration-300 select-none cursor-pointer group
                                    ${comment.isLiked ? 'bg-rm-blue text-white' : 'bg-blue-100 text-pale'}
                                    ${isLikeLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {comment.totalLikes || 0}

                        <AppIcon
                            iconClassName={isLikeLoading ? "fa-spinner fa-spin" : "fa-thumbs-up"}
                            type="regular"
                            className={`text-rm-12 ml-rm-5 group-hover:text-rm-blue ${isLikeLoading ? '' : 'group-hover:animate-bounce'}
                                        ${comment.isLiked ? 'text-white' : 'text-pale'}`}
                        />
                    </button>
                </div>
            }
        </article>
    )
}

export function Answer({comment, onUpdateComment, articleParams}: {
    comment: IWPComment,
    onUpdateComment: (commentId: string, updates: Partial<IWPComment>) => void,
    articleParams?: ArticleTrackingParams
}) {
    return (
        <div className="ml-6 flex flex-col gap-2.5 rounded-2xl bg-neutral px-5 py-4">
            <div className="flex flex-wrap items-center gap-2.5">
                <div className="flex size-8 items-center justify-center rounded-full bg-white">
                    <Image src={logoRM} alt={`Reassurez-moi`} width={20} height={20} />
                </div>

                <span className="font-bold">L’équipe Réassurez-moi</span>

                <span className="rounded-full bg-rm-blue/20 px-2.5 text-xs leading-[22px] text-rm-blue-deep">Expert</span>

                <span className="text-sm text-rm-gray">Le {getFormattedDate(comment.date)}</span>
            </div>

            <div dangerouslySetInnerHTML={{__html: decodeHTMLEntities(comment.commentContent)}} />

            {comment.response &&
                <Comment comment={comment.response} onUpdateComment={onUpdateComment} isRoot={false} articleParams={articleParams} />
            }
        </div>
    )
}
